// Owner: shell-nav (plan §3.1). The rail's width: 240 px, or 56 px when collapsed with ⌘\ (persisted
// per member on the server, mirrored in this browser for the first paint) or automatically below
// 1200 px while the member has not chosen. A collapsed rail peeks open on hover or keyboard focus.
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { updateRailStateContract } from '@dataroom/contracts/nav';
import type { NavDTO, RailState } from '@dataroom/core';
import { api } from '../lib/api';
import { qk, useNav } from '../lib/queries';

const WIDE_QUERY = '(min-width: 1200px)';
const STORAGE_KEY = 'dr.rail.collapsed';

function readStored(): boolean | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'true' ? true : v === 'false' ? false : null;
  } catch {
    return null;
  }
}
function writeStored(v: boolean | null) {
  try {
    if (v === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(v));
  } catch {
    // Storage unavailable (private window): the server keeps the choice.
  }
}

function useWide(): boolean {
  return useSyncExternalStore(
    (l) => {
      const mq = window.matchMedia(WIDE_QUERY);
      mq.addEventListener('change', l);
      return () => mq.removeEventListener('change', l);
    },
    () => window.matchMedia(WIDE_QUERY).matches,
    () => true,
  );
}

// -- An editor takes the width -------------------------------------------------------------------------
// A builder (dashboards, ...) folds the rail to its icons while it is open, so its canvas has the
// room; the member's own choice is not touched and comes back when the editor closes.
let editorFocus = 0;
const editorFocusListeners = new Set<() => void>();
const notifyEditorFocus = () => editorFocusListeners.forEach((l) => l());

/** Fold the rail while `active` (an open editor). */
export function useRailEditorFocus(active = true): void {
  useEffect(() => {
    if (!active) return;
    editorFocus++;
    notifyEditorFocus();
    return () => {
      editorFocus--;
      notifyEditorFocus();
    };
  }, [active]);
}

/** Whether an editor asks the rail to fold. */
export function useRailEditorFocused(): boolean {
  return useSyncExternalStore(
    (l) => {
      editorFocusListeners.add(l);
      return () => editorFocusListeners.delete(l);
    },
    () => editorFocus > 0,
    () => false,
  );
}

// -- An editor owns the phone's bottom edge ---------------------------------------------------------------
// On a phone an open editor has its own action bar at the bottom; the app's tab bar steps aside (the
// editor's back arrow leads up), so the two never stack into one 120 px band.
let bottomOwners = 0;
const bottomOwnerListeners = new Set<() => void>();
const notifyBottomOwner = () => bottomOwnerListeners.forEach((l) => l());

/** The phone tab bar steps aside while `active` (an open editor with its own bottom action bar). */
export function useEditorOwnsBottomBar(active = true): void {
  useEffect(() => {
    if (!active) return;
    bottomOwners++;
    notifyBottomOwner();
    return () => {
      bottomOwners--;
      notifyBottomOwner();
    };
  }, [active]);
}

/** Whether an editor has taken the phone's bottom edge. */
export function useEditorBottomBarOwned(): boolean {
  return useSyncExternalStore(
    (l) => {
      bottomOwnerListeners.add(l);
      return () => bottomOwnerListeners.delete(l);
    },
    () => bottomOwners > 0,
    () => false,
  );
}

/** Whether the rail is collapsed with this choice at this width. */
export function railCollapsed(choice: boolean | null, wide: boolean): boolean {
  return choice ?? !wide;
}

export type RailControl = {
  collapsed: boolean;
  /** The member's own choice (null = automatic by width). */
  choice: boolean | null;
  toggle: () => void;
  collapsedGroups: readonly string[];
  toggleGroup: (key: string) => void;
};

/**
 * Changes on their way to the server. A `/nav` answer computed before the change landed (the first
 * load, a refetch racing the PATCH) must not undo what the member just did: the pending change wins
 * until the server has it.
 */
let pending: { patch: Partial<RailState>; seq: number } | null = null;
let seq = 0;

const sameGroups = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((g, i) => g === b[i]);

function withPending(rail: RailState): RailState {
  return pending ? { ...rail, ...pending.patch } : rail;
}

export function useRailState(): RailControl {
  const qc = useQueryClient();
  const nav = useNav().data;
  const wide = useWide();
  const [stored, setStored] = useState(readStored);
  const choice = nav ? withPending(nav.rail).collapsed : (pending?.patch.collapsed ?? stored);
  // Keep this browser's mirror in step with the server (and a stale answer in step with the change
  // still on its way).
  useEffect(() => {
    if (!nav) return;
    const rail = withPending(nav.rail);
    if (rail.collapsed !== nav.rail.collapsed || !sameGroups(rail.collapsedGroups, nav.rail.collapsedGroups))
      qc.setQueryData<NavDTO>(qk.nav, (old) => (old ? { ...old, rail } : old));
    writeStored(rail.collapsed);
    setStored(rail.collapsed);
  }, [nav, qc]);

  const save = useCallback(
    (patch: Partial<RailState>) => {
      const mine = ++seq;
      pending = { patch: { ...pending?.patch, ...patch }, seq: mine };
      qc.setQueryData<NavDTO>(qk.nav, (old) => (old ? { ...old, rail: { ...old.rail, ...patch } } : old));
      if (patch.collapsed !== undefined) {
        writeStored(patch.collapsed);
        setStored(patch.collapsed);
      }
      void api
        .call(updateRailStateContract, { body: patch })
        .catch(() => {
          // Not saved: the next /nav brings the stored state back.
        })
        .finally(() => {
          if (pending?.seq === mine) pending = null;
        });
    },
    [qc],
  );

  const collapsed = railCollapsed(choice, wide);
  const groups = nav?.rail.collapsedGroups ?? [];
  return {
    collapsed,
    choice,
    toggle: () => save({ collapsed: !collapsed }),
    collapsedGroups: groups,
    toggleGroup: (key) =>
      save({ collapsedGroups: groups.includes(key) ? groups.filter((g) => g !== key) : [...groups, key] }),
  };
}
